import { useRef, useState, useMemo } from 'react'
import { useBrands, useProducts, useSubscriptions } from '../../hooks/useApi'
import { useGenerateCampaign, useSaveCampaign } from '../../hooks/useMarketing'
import { Button } from '../../components/ui/Button'
import { Card, CardContent } from '../../components/ui/Card'
import { BannerCanvas } from '../../components/marketing/BannerCanvas'
import { ShareCenter } from '../../components/marketing/ShareCenter'
import { PROMO_TYPES, BANNER_TEMPLATES } from '../../lib/marketing/constants'
import { buildBannerData, generateQrDataUrl, CAMPAIGN_BANNER_SIZES } from '../../lib/marketing/utils'
import { exportElementAsImage, downloadDataUrl } from '../../lib/marketing/share'
import { normalizePlanId } from '../../lib/plans'
import { Wand2, Save } from 'lucide-react'
import type { BannerSizeId } from '../../lib/marketing/constants'
import { AiBoostExhaustedDialog, isAiBoostExhausted } from '../../components/marketing/AiBoostExhaustedDialog'

type CampaignResult = {
  content: {
    title: string
    captions: { instagram: string; facebook: string; whatsapp: string }
    hashtags: string
    cta: string
  }
  catalogLink: string
  banners: Record<string, string>
  qrCodeDataUrl: string
}

export default function MarketingCampaign() {
  const { data: brands } = useBrands()
  const { data: products } = useProducts()
  const { data: subscriptions } = useSubscriptions()
  const plan = normalizePlanId(subscriptions?.[0]?.plan)

  const [brandId, setBrandId] = useState<number | ''>('')
  const [productId, setProductId] = useState<number | ''>('')
  const [promoType, setPromoType] = useState('flash_sale')
  const [result, setResult] = useState<CampaignResult | null>(null)
  const [canvasRenderData, setCanvasRenderData] = useState<ReturnType<typeof buildBannerData> | null>(null)
  const [generating, setGenerating] = useState(false)
  const [currentSize, setCurrentSize] = useState<BannerSizeId>('instagram_feed')
  const [boostExhausted, setBoostExhausted] = useState(false)

  const canvasRef = useRef<HTMLDivElement>(null)
  const renderDataRef = useRef<ReturnType<typeof buildBannerData> | null>(null)
  const generateCampaign = useGenerateCampaign()
  const saveCampaign = useSaveCampaign()

  const brand = brands?.find((b: { id: number }) => b.id === brandId)
  const brandProducts = products?.filter((p: { brandId: number }) => p.brandId === brandId) ?? []
  const product = brandProducts.find((p: { id: number }) => p.id === productId)
  const template = BANNER_TEMPLATES[0]

  const bannerData = useMemo(() => {
    if (!brand || !product) return null
    return buildBannerData({
      brand,
      product,
      promoType,
      plan,
      catalogUrl: result?.catalogLink,
      qrDataUrl: result?.qrCodeDataUrl,
    })
  }, [brand, product, promoType, plan, result])

  const handleGenerate = async () => {
    if (!brand || !product) return
    setGenerating(true)
    try {
      const aiResult = await generateCampaign.mutateAsync({
        brandId: brand.id,
        productId: product.id,
        promoType,
      })

      const catalogLink = aiResult.catalogLink
      const qrCodeDataUrl = await generateQrDataUrl(catalogLink)
      const renderData = buildBannerData({
        brand,
        product,
        promoType,
        plan,
        catalogUrl: catalogLink,
        qrDataUrl: qrCodeDataUrl,
      })
      renderDataRef.current = renderData
      setCanvasRenderData(renderData)
      await new Promise((r) => setTimeout(r, 150))

      const banners: Record<string, string> = {}
      for (const sizeKey of CAMPAIGN_BANNER_SIZES) {
        setCurrentSize(sizeKey)
        await new Promise((r) => setTimeout(r, 400))
        const el = canvasRef.current
        if (!el) continue
        const { BANNER_SIZES } = await import('../../lib/marketing/constants')
        const size = BANNER_SIZES[sizeKey]
        banners[sizeKey] = await exportElementAsImage(el, 'png', size.width, size.height)
      }

      setResult({
        content: aiResult.content,
        catalogLink,
        banners,
        qrCodeDataUrl,
      })
      setCurrentSize('instagram_feed')
    } catch (e) {
      if (isAiBoostExhausted(e)) setBoostExhausted(true)
    } finally {
      setGenerating(false)
    }
  }

  const handleSave = async () => {
    if (!brand || !product || !result) return
    await saveCampaign.mutateAsync({
      brandId: brand.id,
      productId: product.id,
      promoType,
      title: result.content.title,
      banners: result.banners,
      captions: result.content.captions,
      hashtags: result.content.hashtags,
      qrCodeDataUrl: result.qrCodeDataUrl,
      catalogLink: result.catalogLink,
      cta: result.content.cta,
    })
  }

  const fullCaption = result
    ? `${result.content.captions.instagram}\n\n${result.content.hashtags}\n\n${result.content.cta}`
    : ''

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Campaign</h1>
        <p className="text-gray-600 mt-1">Buat paket promosi lengkap dengan AI (2 AI Boost)</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Brand</label>
              <select className="w-full border rounded-lg px-3 py-2" value={brandId} onChange={(e) => { setBrandId(Number(e.target.value) || ''); setProductId('') }}>
                <option value="">Pilih Brand</option>
                {brands?.map((b: { id: number; name: string }) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Produk</label>
              <select className="w-full border rounded-lg px-3 py-2" value={productId} onChange={(e) => setProductId(Number(e.target.value) || '')} disabled={!brandId}>
                <option value="">Pilih Produk</option>
                {brandProducts.map((p: { id: number; name: string }) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Jenis Promosi</label>
              <select className="w-full border rounded-lg px-3 py-2" value={promoType} onChange={(e) => setPromoType(e.target.value)}>
                {PROMO_TYPES.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleGenerate} disabled={!brand || !product || generating}>
            <Wand2 className="w-4 h-4 mr-2" />
            {generating ? 'Membuat Campaign...' : 'Generate Campaign (2 AI Boost)'}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold">{result.content.title}</h2>
                <Button size="sm" onClick={handleSave} disabled={saveCampaign.isPending}>
                  <Save className="w-4 h-4 mr-1" /> Simpan
                </Button>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {Object.entries(result.banners).map(([key, url]) => (
                  <div key={key}>
                    <p className="text-sm font-medium text-gray-600 mb-2 capitalize">{key.replace(/_/g, ' ')}</p>
                    <img src={url} alt={key} className="w-full rounded-lg border" />
                    <Button variant="outline" size="sm" className="mt-2" onClick={() => downloadDataUrl(url, `campaign-${key}.png`)}>
                      Download
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold">Caption Instagram</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{result.content.captions.instagram}</p>
                </div>
                <div>
                  <h4 className="font-semibold">Caption Facebook</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{result.content.captions.facebook}</p>
                </div>
                <div>
                  <h4 className="font-semibold">Caption WhatsApp</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{result.content.captions.whatsapp}</p>
                </div>
                <div>
                  <h4 className="font-semibold">Hashtag</h4>
                  <p className="text-sm text-indigo-600">{result.content.hashtags}</p>
                </div>
                <div>
                  <h4 className="font-semibold">Call To Action</h4>
                  <p className="text-sm text-gray-700">{result.content.cta}</p>
                </div>
                <div>
                  <h4 className="font-semibold">Link Katalog</h4>
                  <a href={result.catalogLink} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline">
                    {result.catalogLink}
                  </a>
                </div>
                {result.qrCodeDataUrl && (
                  <div>
                    <h4 className="font-semibold mb-2">QR Code</h4>
                    <img src={result.qrCodeDataUrl} alt="QR" className="w-32 h-32 border rounded-lg" />
                  </div>
                )}
              </div>

              <div className="mt-6 pt-4 border-t">
                <ShareCenter
                  caption={fullCaption}
                  imageDataUrl={result.banners.instagram_feed}
                  filename="campaign-instagram.png"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {bannerData && (
        <BannerCanvas
          ref={canvasRef}
          layout={template.layout}
          variant={template.variant}
          sizeId={currentSize}
          data={renderDataRef.current || canvasRenderData || bannerData}
        />
      )}

      <AiBoostExhaustedDialog open={boostExhausted} onClose={() => setBoostExhausted(false)} />
    </div>
  )
}
